import { Button, Icon, Input, InputWrapper } from "@components";
import { PromotionTargetTypeEnum, PromotionTypeEnum } from "@utilities/enums";
import { toIsoDate } from "@utilities/functions";
import { Promotion } from "@utilities/types";
import { FC, useCallback, useMemo, useState } from "react";

interface Props {
    promotion?: Promotion;
    onCancel: () => void;
    onCreatePromotion: (promotion: Partial<Promotion>) => void;
}

/**
 * Formulaire simplifié pour créer un code promo client
 * Le client entre ce code lors du paiement pour bénéficier de la réduction
 */
export const AddPromoCode: FC<Props> = ({
    promotion,
    onCancel,
    onCreatePromotion,
}) => {

    const [code, setCode] = useState<string>(promotion?.code || "");
    const [type, setType] = useState<PromotionTypeEnum>(promotion?.type || PromotionTypeEnum.PERCENTAGE);
    const [value, setValue] = useState<number>(promotion?.value! || 0);
    const [startDate, setStartDate] = useState<string>(promotion?.startDate ? toIsoDate(promotion.startDate) : "");
    const [endDate, setEndDate] = useState<string>(promotion?.endDate ? toIsoDate(promotion.endDate) : "");
    const [note, setNote] = useState<string>(promotion?.note || "");
    const [isSingleUse, setIsSingleUse] = useState<boolean>(promotion?.isSingleUse ?? true);

    const isFormValid = useMemo(() => {
        return code.trim() !== "" && type && value > 0 && startDate && endDate && new Date(endDate) >= new Date(startDate);
    }, [code, type, value, startDate, endDate]);

    const handleCancel = useCallback(() => {
        setCode("");
        setType(PromotionTypeEnum.PERCENTAGE);
        setValue(0);
        setStartDate("");
        setEndDate("");
        setNote("");
        setIsSingleUse(true);
        onCancel();
    }, [onCancel]);

    const handleCreatePromotion = useCallback(async () => {
        if (!isFormValid) return;

        onCreatePromotion({
            code: code.toUpperCase(),
            type,
            value,
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            note,
            isSingleUse,
            // Pour un code promo simple, targetType est PRODUCTS mais sans produits sélectionnés
            targetType: PromotionTargetTypeEnum.PRODUCTS,
            targetCategories: [],
            targetProducts: [],
        });
        handleCancel();
    }, [
        code, type, value, startDate,
        endDate, note, isSingleUse, onCreatePromotion,
        handleCancel, isFormValid,
    ]);

    // Prévisualisation de la remise
    const previewExample = useMemo(() => {
        if (!value || value <= 0) return null;
        const examplePrice = 10000;
        if (type === PromotionTypeEnum.PERCENTAGE) {
            const discount = examplePrice * (value / 100);
            return {
                original: examplePrice,
                discount,
                final: examplePrice - discount
            };
        }
        return {
            original: examplePrice,
            discount: value,
            final: Math.max(0, examplePrice - value)
        };
    }, [type, value]);

    return (
        <div className="flex flex-col gap-6">
            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                <Icon iconName="Info" size={18} className="text-blue-600 mt-0.5" />
                <div>
                    <p className="text-sm font-medium text-blue-800">Code promo client</p>
                    <p className="text-xs text-blue-600">
                        Le client entre ce code lors du paiement pour bénéficier de la réduction sur sa commande.
                    </p>
                </div>
            </div>

            {/* Section Code */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Tag" size={16} />
                    Code promotionnel
                </h4>
                <InputWrapper label="Code de la promotion *">
                    <Input
                        placeholder="ex: PROMO2025, SOLDES, NOEL"
                        value={code}
                        onChange={e => setCode(e.target.value.toUpperCase())}
                    />
                </InputWrapper>
                <p className="text-xs text-gray-500 mt-1">
                    Les clients utiliseront ce code pour appliquer la réduction
                </p>
            </div>

            {/* Section Type et Valeur */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Percent" size={16} />
                    Type et valeur de la remise
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputWrapper label="Type de réduction">
                        <Input
                            type="select"
                            placeholder="Type de réduction"
                            value={type}
                            onChange={e => setType(e.target.value as PromotionTypeEnum)}
                            options={[
                                { label: "Pourcentage (%)", value: PromotionTypeEnum.PERCENTAGE },
                                { label: "Montant fixe (Fcfa)", value: PromotionTypeEnum.FIXED },
                            ]}
                        />
                    </InputWrapper>
                    <InputWrapper label={`Valeur ${type === PromotionTypeEnum.PERCENTAGE ? "(%)" : "(Fcfa)"}`}>
                        <Input
                            type="number"
                            min={0}
                            max={type === PromotionTypeEnum.PERCENTAGE ? 100 : undefined}
                            placeholder={type === PromotionTypeEnum.PERCENTAGE ? "ex: 20" : "ex: 1000"}
                            value={value || ''}
                            onChange={e => setValue(Number(e.target.value))}
                        />
                    </InputWrapper>
                </div>

                {/* Prévisualisation */}
                {previewExample && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-500 mb-2">Exemple sur un article à 10 000 Fcfa :</p>
                        <div className="flex items-center gap-2 text-sm">
                            <span className="line-through text-gray-400">{previewExample.original.toLocaleString()} Fcfa</span>
                            <Icon iconName="ArrowRight" size={14} className="text-gray-400" />
                            <span className="font-bold text-green-600">{previewExample.final.toLocaleString()} Fcfa</span>
                            <span className="text-xs text-green-600">(-{previewExample.discount.toLocaleString()} Fcfa)</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Section Dates */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Calendar" size={16} />
                    Période de validité
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputWrapper label="Date de début">
                        <Input
                            type="date"
                            placeholder="ex: 01/01/2025"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                        />
                    </InputWrapper>
                    <InputWrapper label="Date de fin">
                        <Input
                            type="date"
                            placeholder="ex: 01/01/2025"
                            min={startDate}
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                        />
                    </InputWrapper>
                </div>
                {startDate && endDate && new Date(endDate) < new Date(startDate) && (
                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                        <Icon iconName="AlertCircle" size={12} />
                        La date de fin doit être après la date de début
                    </p>
                )}
            </div>

            {/* Section Options */}
            <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Icon iconName="Settings" size={16} />
                    Options
                </h4>

                {/* Type d'usage */}
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200">
                    <input
                        id="singleUseCode"
                        type="checkbox"
                        className="checkbox checkbox-primary mt-0.5"
                        checked={isSingleUse}
                        onChange={(e) => setIsSingleUse(e.target.checked)}
                    />
                    <label htmlFor="singleUseCode" className="cursor-pointer">
                        <span className="text-sm font-medium text-gray-700">Usage unique</span>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {isSingleUse
                                ? "Le code sera désactivé après sa première utilisation"
                                : "Le code peut être utilisé plusieurs fois pendant sa période de validité"
                            }
                        </p>
                    </label>
                </div>

                {/* Note */}
                <div className="mt-4">
                    <InputWrapper label="Note interne (optionnel)">
                        <Input
                            type="textarea"
                            placeholder="ex: Code pour les nouveaux clients"
                            value={note}
                            onChange={e => setNote(e.target.value)}
                        />
                    </InputWrapper>
                </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-between pt-2 border-t">
                <Button
                    variant="secondary"
                    onClick={handleCancel}
                >
                    Annuler
                </Button>

                <Button
                    variant="success"
                    onClick={handleCreatePromotion}
                    disabled={!isFormValid}
                >
                    <Icon iconName={promotion ? "Check" : "Plus"} size={16} className="mr-1" />
                    {promotion ? 'Mettre à jour' : 'Créer le code promo'}
                </Button>
            </div>
        </div>
    )
}
